// mui
import { Grid, SxProps } from '@mui/material';

// i18n
import { useTranslation } from 'react-i18next';

// theme
import { theme } from '../../../theme';

// ui
import { Empty } from '../Empty/Empty';
import { IconCircleProps } from '../IconCircle/IconCircle';

export type BlockchainProcessingStatus = 'LOADING' | 'SUCCESS';

export interface BlockchainProcessingProps {
	/** LOADING shows the spinning cube; SUCCESS swaps to a checkmark. */
	status: BlockchainProcessingStatus;
	loadingTitle?: string;
	loadingContent?: string;
	successTitle?: string;
	successContent?: string;
}

/**
 * Inline blockchain-transaction animation, mirroring the v1 "Recording
 * Transaction" state (Empty + spinning Cube) used in FormAddTransaction. Render
 * this in place of a dialog/form body while a v2 chain write is in flight, then
 * flip to `status="SUCCESS"` when the write completes.
 */
export const BlockchainProcessing = ({
	status,
	loadingTitle,
	loadingContent,
	successTitle,
	successContent,
}: BlockchainProcessingProps) => {
	// hooks
	const { t } = useTranslation();

	// vars
	const isSuccess = status === 'SUCCESS';

	const iconCircleProps: IconCircleProps = {
		size: 'xl',
		type: isSuccess ? 'check' : 'cube',
	};

	const sxBox: SxProps = isSuccess
		? { marginTop: 0, marginBottom: 0 }
		: { marginTop: theme.spacing(4), marginBottom: theme.spacing(4) };

	const title = {
		children: isSuccess
			? (successTitle ?? t('blockchain.processing.success.title', 'Transaction Complete'))
			: (loadingTitle ?? t('blockchain.processing.loading.title', 'Recording Transaction')),
	};

	const content = {
		children: isSuccess
			? (successContent ??
				t(
					'blockchain.processing.success.content',
					'The transaction has been recorded on the blockchain network.'
				))
			: (loadingContent ??
				t(
					'blockchain.processing.loading.content',
					'Transaction is being recorded on the blockchain network...'
				)),
	};

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Empty content={content} IconCircleProps={iconCircleProps} sxBox={sxBox} title={title} />
			</Grid>
		</Grid>
	);
};
